/**
 * AAIKO product page behaviour.
 *
 * Three jobs, all scoped to <aaiko-pdp>:
 *
 *  1. Variant selection. The drawn load state (1210:25403) has nothing chosen,
 *     so selection is written to the URL as ?variant= and the Liquid-rendered
 *     regions marked [data-aaiko-sync] are re-fetched through the Section
 *     Rendering API. That keeps aaiko-price and the button copy authoritative in
 *     Liquid instead of duplicating money formatting and state rules in JS. The
 *     details drawer sits outside those regions, so its open row survives.
 *
 *  2. Add to bag, against Horizon's cart. The POST and the CartLinesUpdateEvent
 *     handshake mirror assets/product-form.js so the cart drawer, the cart icon
 *     and the header count all react exactly as they do everywhere else.
 *
 *  3. The mobile media rail (1210:25877): the thumb tracks scroll position.
 *
 * Favourites are NOT handled here. The heart is <aaiko-fav-toggle> from
 * assets/aaiko-favourites.js, which owns the store for every surface. Keeping
 * a second copy of that state on the PDP is how the two drift apart.
 */

import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

const FEEDBACK_MS = 2000;

/** Drawn nav row, 1210:21883. Both breakpoints: only the inset changes. */
const NAV_HEIGHT = 44;

/**
 * snippets/scripts.liquid declares `const Theme = {...}` in a classic script, so
 * it lives on the global LEXICAL scope, not on window. window.Theme is
 * undefined; the bare identifier is what Horizon's own modules read.
 * @returns {{cart_add_url: string, cart_url: string}}
 */
function cartRoutes() {
  const fallback = { cart_add_url: '/cart/add.js', cart_url: '/cart' };
  try {
    // eslint-disable-next-line no-undef
    return (typeof Theme !== 'undefined' && Theme.routes) || fallback;
  } catch {
    return fallback;
  }
}

/** @returns {Record<string, string>} */
function themeTranslations() {
  try {
    // eslint-disable-next-line no-undef
    return (typeof Theme !== 'undefined' && Theme.translations) || {};
  } catch {
    return {};
  }
}

class AaikoPdp extends HTMLElement {
  #feedbackTimer = null;
  /** @type {IntersectionObserver | null} */
  #navObserver = null;
  /** @type {HTMLElement | null} */
  #stickyBar = null;
  /** @type {IntersectionObserver | null} */
  #stickyObserver = null;
  /** @type {MutationObserver | null} */
  #stickyOrderObserver = null;
  /** @type {IntersectionObserver | null} */
  #stickyFooterObserver = null;
  /** Has the real buy row left the top of the screen? */
  #atbPassed = false;
  /** Is the footer on screen, i.e. is the bar sitting on top of it? */
  #footerReached = false;
  /** @type {MediaQueryList | null} */
  #sheetMedia = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('submit', this.#onNotifySubmit);
    this.addEventListener('keydown', this.#onKeydown);
    this.#syncRovingTabindex();

    const track = this.#track;
    if (track) {
      track.addEventListener('scroll', this.#onScroll, { passive: true });
      window.addEventListener('resize', this.#onScroll, { passive: true });
      this.#onScroll();
    }

    this.#watchNavOverlay();
    this.#setupStickyAtb();
    this.#setupSizeSheet();
  }

  /* The subscription itself still belongs to Klaviyo: this posts to their
     documented onsite back-in-stock endpoint with the shop's company id, so the
     list, the flow and the trigger stay in Klaviyo exactly as they are. Only
     the popup around it is ours (see snippets/aaiko-notify-popup.liquid).

     Not verified against the live Klaviyo account - the endpoint answers 200
     for a well-formed request and this has only been exercised against the
     build theme. One real submission should be checked before launch; the
     failure is visible rather than silent, because a non-200 shows the error
     line instead of the success one. */
  #onNotifySubmit = (event) => {
    const form = event.target.closest('[data-aaiko-notify-form]');
    if (!form) return;
    event.preventDefault();

    const dialog = form.closest('[data-aaiko-notify-popup]');
    const email = form.querySelector('[data-aaiko-notify-email]');
    const submit = form.querySelector('[data-aaiko-notify-submit]');
    const note = form.querySelector('[data-aaiko-notify-note]');
    if (!(dialog instanceof HTMLElement) || !(email instanceof HTMLInputElement)) return;

    if (!email.checkValidity()) {
      dialog.dataset.state = 'error';
      if (note) note.textContent = note.dataset.error || note.textContent;
      email.focus();
      return;
    }

    const body = new URLSearchParams({
      a: dialog.dataset.company || '',
      email: email.value,
      variant: dialog.dataset.variant || '',
      product: dialog.dataset.product || '',
      platform: 'shopify'
    });

    submit?.setAttribute('aria-busy', 'true');
    fetch('https://a.klaviyo.com/onsite/components/back-in-stock/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    })
      .then((r) => {
        dialog.dataset.state = r.ok ? 'done' : 'error';
        if (note) note.textContent = r.ok ? note.dataset.done || '' : note.dataset.error || '';
      })
      .catch(() => {
        dialog.dataset.state = 'error';
        if (note) note.textContent = note.dataset.error || '';
      })
      .finally(() => submit?.removeAttribute('aria-busy'));
  };

  disconnectedCallback() {
    window.removeEventListener('resize', this.#onScroll);
    this.#navObserver?.disconnect();
    this.#stickyObserver?.disconnect();
    this.#stickyOrderObserver?.disconnect();
    this.#stickyFooterObserver?.disconnect();
    /* The bar was moved to <body>, so it does not go away with this element.
       Editor re-renders would otherwise stack one bar per render. */
    this.#stickyBar?.remove();
    if (this.#feedbackTimer) clearTimeout(this.#feedbackTimer);
  }

  /* ---------------------------------------------------------------------
     Sticky add to bag (drawn 1210:25807), rebuilt 31/08/2026 on the mango.com
     pattern AAIKO asked for: "pas laten zien als de ander uit beeld is en met
     titel en prijs, en dan na selecteer je maat een klein inschuivend popupje."

     The bar itself is unchanged in kind - still a PROXY that owns no form
     state and forwards its click to whatever is live in the real buy row,
     which keeps one variant input, one Klaviyo back-in-stock trigger (Klaviyo
     binds one per rendered product) and one [data-aaiko-sync] region in the
     page. What changed is what it says, when it says it, and what pressing it
     does with no size chosen. Each of those replaces an earlier decision:

       title + price   the bar used to be a size row over a button
       out of view     it used to appear at the halfway line (30/08)
       the sheet       the sizes used to sit in the bar itself (30/08)

     Moving it to <body> is what makes any of it possible: `.aaiko-pdp` has
     container-type: inline-size, which brings layout containment, a stacking
     context and a containing block for fixed children with it. Inside it, the
     bar could neither pin to the viewport nor paint over Klaviyo's button. On
     <body> it can do both. The SHEET has the opposite requirement and stays
     put - see #setupSizeSheet.

     Fails closed: no IntersectionObserver, no bar. It ships `hidden`, so a
     blocked script leaves nothing behind.
     --------------------------------------------------------------------- */
  #setupStickyAtb() {
    const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-aaiko-sticky-atb]'));
    if (!bar || !('IntersectionObserver' in window)) return;

    this.#stickyBar = bar;
    document.body.appendChild(bar);
    bar.hidden = false;

    /* The one viewport breakpoint this file reads, and it reads it for the
       same reason the bar's CSS does: on <body> there is no AAIKO container
       left above the bar to ask. 749 is the house switch. */
    this.#sheetMedia = window.matchMedia('(max-width: 749px)');

    bar.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      if (!target.closest('[data-aaiko-sticky-atb-button]')) return;
      event.preventDefault();

      /* No size chosen yet. On a phone the size row is a screen and a half
         away, so the sizes come to the shopper as a sheet. At 1440 the buy
         panel is position: sticky and the row is a short scroll up, which is
         where mango.com sends you at that width too - and where the real
         button's own data-aaiko-needs-size path already goes. */
      const real = this.querySelector('[data-aaiko-atb]');
      if (
        real instanceof HTMLElement &&
        real.hasAttribute('data-aaiko-needs-size') &&
        this.#sheetMedia?.matches &&
        this.#openSizeSheet()
      ) {
        return;
      }

      /* Whatever is live in the real row right now. Klaviyo hides our button
         with an inline display:none and injects its own, so "the button" is
         resolved per click rather than cached. */
      this.#liveAtbControl?.click();
    });

    /* AAIKO 31/08: "pas laten zien als de ander uit beeld is."

       The line is the top of the screen, less the pinned nav: the bar arrives
       the moment the real buy row is no longer anywhere a shopper could reach
       it, and it goes away again if they scroll back up to it. That is a
       stricter rule than the 30/08 one it replaces, which brought the bar up
       while the real button was still on screen at the halfway mark - two live
       add to bag buttons in one viewport, which is what this request is about.

       `rootMargin: -44px 0 0 0` shrinks the observer's root from the top by the
       nav, so `rootBounds.top` IS that line. The test is then one expression -
       has the row's bottom edge passed above it - and it reads the same whether
       the row is straddling the line or left the screen a thousand pixels ago,
       so no scroll handler is needed to hold the answer. */
    const row = this.querySelector('.aaiko-pdp__atb');
    this.#stickyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const line = entry.rootBounds ? entry.rootBounds.top : NAV_HEIGHT;
          this.#atbPassed = entry.boundingClientRect.bottom <= line;
        }
        this.#syncStickyAtb();
      },
      { rootMargin: `-${NAV_HEIGHT}px 0px 0px 0px`, threshold: 0 }
    );
    if (row) this.#stickyObserver.observe(row);

    /* CLIENT REPORT (feedback 09/09/2026): "sticky bar op PDP desktop zorgt
       ervoor dat je niet bij de language switcher kan. zorg dat hij bij de
       footer weer verdwijnt."

       The bar is fixed to the bottom of the viewport with nothing below it, so
       at the end of the page it parks on top of the footer's last row - the one
       carrying the copyright and the language <select>. The select is then
       literally unreachable: the bar's own backdrop takes the click.

       So the footer is a second, independent stop condition. The moment ANY
       part of it enters the viewport the bar stands down and stays down, and it
       comes back on the way up. Watching the whole footer rather than just its
       bottom row is deliberate: the bar would otherwise hand the row back at the
       exact pixel it starts to appear, which is a flicker rather than a
       handover, and by the time a shopper is in the footer the buy row is a long
       way behind them anyway.

       Queried from `document`: the bar lives on <body> and the footer is its own
       section outside this element, so `this.querySelector` would find neither.
       A page with no AAIKO footer (an app or editor render) simply keeps the old
       behaviour - `#footerReached` stays false. */
    const footer = document.querySelector('.aaiko-footer');
    if (footer) {
      this.#stickyFooterObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) this.#footerReached = entry.isIntersecting;
          this.#syncStickyAtb();
        },
        { threshold: 0 }
      );
      this.#stickyFooterObserver.observe(footer);
    }

    /* Klaviyo mounts its own floating button after we have mounted ours, and
       both sit at the top of the z range, where equal values are settled by
       DOM order. So anything appended to <body> after us puts us back last
       while the bar is showing. Terminates by construction: the re-append
       makes the bar last, and the next callback then finds nothing to do. */
    this.#stickyOrderObserver = new MutationObserver(() => {
      if (!bar.hasAttribute('data-shown')) return;
      if (bar === document.body.lastElementChild) return;
      document.body.appendChild(bar);
    });
    this.#stickyOrderObserver.observe(document.body, { childList: true });

    this.#syncStickyAtbLabel();
    this.#syncStickyPrice();
  }

  /** The control the real buy row is currently offering, ours or Klaviyo's. */
  get #liveAtbControl() {
    const ours = /** @type {HTMLElement | null} */ (this.querySelector('[data-aaiko-atb]'));
    if (ours && ours.offsetParent !== null) return ours;
    const klaviyo = /** @type {HTMLElement | null} */ (
      this.querySelector('#klaviyo-bis-button-container button, [klaviyo-bis-trigger] button:not([data-aaiko-atb])')
    );
    return klaviyo || ours;
  }

  #syncStickyAtb() {
    const bar = this.#stickyBar;
    if (!bar) return;
    const show = this.#atbPassed && !this.#footerReached;
    if (show) {
      /* Re-appended on every show: the bar and Klaviyo's own sticky button both
         sit at the top of the z range, and equal z-index is settled by DOM
         order. Klaviyo injects late, so being last is the only reliable way to
         stay above it. */
      if (bar.parentElement !== document.body || bar !== document.body.lastElementChild) {
        document.body.appendChild(bar);
      }
      bar.setAttribute('data-shown', '');
    } else {
      bar.removeAttribute('data-shown');
    }
  }

  /* The price is a CLONE of the panel's, re-taken after every variant change.
     Rendering it a second time in Liquid would mean a second set of arguments
     to keep in step with the first; a clone of the region that is already
     authoritative cannot say something different from it. The 30-day reference
     line comes along and is hidden in CSS rather than stripped here, so this
     stays a copy rather than an edit. */
  #syncStickyPrice() {
    const bar = this.#stickyBar;
    if (!bar) return;
    const slot = bar.querySelector('[data-aaiko-sticky-price]');
    const real = this.querySelector('[data-aaiko-sync="price"] .aaiko-price');
    if (!(slot instanceof HTMLElement) || !real) return;
    slot.replaceChildren(real.cloneNode(true));
  }

  /* ---------------------------------------------------------------------
     The size sheet (AAIKO 31/08: "een klein inschuivend popupje").

     Deliberately NOT moved to <body> the way the bar is. A modal <dialog> is
     laid out in the top layer, against the viewport, so `container-type` on
     .aaiko-pdp cannot trap it and no z-index can paint over it - including
     Klaviyo's. Leaving it in the section is what lets the size buttons inside
     it bubble to this element, where #onClick's picker contract, #onKeydown's
     roving arrows and #syncRovingTabindex already apply to them unchanged.

     Fails closed twice over: no <dialog> support, or no size row, and
     #openSizeSheet returns false and the bar falls back to the real button,
     which sends the shopper to the row itself.
     --------------------------------------------------------------------- */
  #setupSizeSheet() {
    const sheet = this.#sizeSheet;
    if (!sheet) return;

    /* Escape would close the dialog outright and skip the slide out. Taking
       the cancel and running the same close path keeps one way out. */
    sheet.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#closeSizeSheet();
    });
    /* Whatever route closed it, the open flag has to go with it or the next
       open would find the panel already in place and never travel. */
    sheet.addEventListener('close', () => sheet.removeAttribute('data-open'));

    sheet.addEventListener('click', async (event) => {
      const target = /** @type {HTMLElement} */ (event.target);

      if (target.closest('[data-aaiko-sizesheet-close]')) {
        event.preventDefault();
        this.#closeSizeSheet();
        return;
      }
      /* The dialog's own box is the full viewport and the sheet is the panel
         inside it, so a hit that lands on the dialog element is above the
         sheet. Same test .aaiko-pdp__sizechart makes. */
      if (target === sheet) {
        this.#closeSizeSheet();
        return;
      }

      const size = target.closest('[data-aaiko-option-value]');
      if (!(size instanceof HTMLElement)) return;
      /* Taken here rather than left to #onClick: this listener is on a
         descendant, so it runs first, and the sheet needs to do two things
         after the selection that the picker's own handler knows nothing
         about. Everything else in the sheet - the size guide button - is left
         to bubble. */
      event.preventDefault();
      event.stopPropagation();

      if (size.dataset.available === 'false' && size.dataset.aaikoNotify !== 'true') return;
      if (!size.classList.contains('is-selected')) await this.#selectValue(size);

      /* Mango adds the garment on the size tap rather than making you press
         a second button, and AAIKO asked for it to work exactly that way. The
         click goes to whatever the real row is offering now, which is the same
         contract the bar has: the buy button on an available size, Klaviyo's
         notify control on a sold-out one, nothing at all when the row is
         genuinely disabled. */
      this.#closeSizeSheet({ immediate: true });
      this.#liveAtbControl?.click();
    });

    this.#syncSheetSizes();
  }

  /** @returns {HTMLDialogElement | null} */
  get #sizeSheet() {
    const el = this.querySelector('[data-aaiko-sizesheet]');
    return el instanceof HTMLDialogElement ? el : null;
  }

  /** @returns {boolean} whether the sheet actually opened */
  #openSizeSheet() {
    const sheet = this.#sizeSheet;
    if (!sheet || typeof sheet.showModal !== 'function') return false;
    if (!sheet.querySelector('[data-aaiko-option-value]')) return false;
    if (sheet.open) return true;

    sheet.showModal();
    /* showModal() puts focus on the first focusable thing it finds, which here
       is the size guide - a shopper who came for a size lands on the link that
       takes them somewhere else. The sizes are what the sheet is for, so the
       row's own tab stop takes it instead. */
    const stop = sheet.querySelector('[data-aaiko-option-value][tabindex="0"]');
    if (stop instanceof HTMLElement) stop.focus({ preventScroll: true });
    /* The panel is drawn off the bottom edge and only released on the next
       frame: set in the same frame as showModal() there is no earlier value to
       transition from and the sheet would simply appear. */
    requestAnimationFrame(() => sheet.setAttribute('data-open', ''));
    return true;
  }

  /**
   * @param {{ immediate?: boolean }} [options] `immediate` skips the slide out
   *   and closes in the same tick. Used when the sheet is handing off to
   *   something that needs the page back NOW: a modal <dialog> makes the rest
   *   of the document inert, and the cart drawer is a plain custom element, so
   *   a sheet still animating out would swallow every click on the drawer it
   *   just opened.
   */
  #closeSizeSheet({ immediate = false } = {}) {
    const sheet = this.#sizeSheet;
    if (!sheet || !sheet.open) return;
    sheet.removeAttribute('data-open');
    if (immediate) {
      sheet.close();
      return;
    }

    /* close() is held until the panel has travelled back down, otherwise the
       dialog leaves the top layer mid-slide and the sheet vanishes. The timer
       is the backstop for a transition that never fires - a display change, a
       browser that skips it - so the sheet can never be left stuck open. */
    const panel = sheet.querySelector('.aaiko-pdp__sizesheet-panel');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      if (sheet.open) sheet.close();
    };
    if (panel) panel.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 700);
  }

  /* The sheet's size row (AAIKO 31/08, replacing the row that used to sit in
     the bar itself).

     CLONED from the real row rather than rendered a second time in Liquid: the
     sizes, their availability, the notify bell on a sold-out one and the
     selected mark are all authored once in snippets/aaiko-variant-picker, and a
     clone cannot drift from them. Re-cloned after every variant change, so the
     mark moves in both rows together.

     The clone keeps its data attributes and its radiogroup, which is what makes
     the picker's keyboard and selection contracts apply to it unchanged. Only
     the ids go: two elements answering to the same name is the one thing a copy
     may not bring with it. */
  #syncSheetSizes() {
    const sheet = this.#sizeSheet;
    if (!sheet) return;
    const slot = sheet.querySelector('[data-aaiko-sheet-sizes]');
    if (!(slot instanceof HTMLElement)) return;

    const real = this.querySelector('[data-aaiko-sync="sizes"] .aaiko-pdp__options--size');
    if (!real) {
      slot.replaceChildren();
      return;
    }
    const clone = /** @type {HTMLElement} */ (real.cloneNode(true));
    for (const el of clone.querySelectorAll('[id]')) el.removeAttribute('id');
    slot.replaceChildren(clone);
    /* The clone is a radiogroup of its own, so it gets its own single tab stop
       rather than the -1 everywhere the bar's inert copy used to take. */
    this.#syncRovingTabindex();
  }

  /* The label is the buy row's whole state - "Kies je maat", "In winkelmand",
     "Uitverkocht" - so it is copied across after every variant change rather
     than rendered twice from Liquid. */
  #syncStickyAtbLabel() {
    const bar = this.#stickyBar;
    if (!bar) return;
    const button = bar.querySelector('[data-aaiko-sticky-atb-button]');
    const real = this.querySelector('[data-aaiko-atb]');
    if (!(button instanceof HTMLElement) || !(real instanceof HTMLElement)) return;
    button.textContent = real.textContent?.trim() || button.textContent;
    button.toggleAttribute('disabled', real.hasAttribute('disabled'));
  }

  /**
   * The nav floats over the media on this template, transparent (see the
   * overlay block in sections/aaiko-product-main.liquid). This reports when
   * there is no longer any photography behind it, and the CSS gives the row
   * its surface back.
   *
   * The root is shrunk from the top by the nav height, so the gallery stops
   * intersecting exactly when its bottom edge passes above the row - no
   * scroll handler, and no viewport height to recompute on resize.
   *
   * Fails open: without IntersectionObserver the nav stays transparent, which
   * is the drawn state.
   */
  #watchNavOverlay() {
    const gallery = this.querySelector('.aaiko-pdp__gallery');
    if (!gallery || !('IntersectionObserver' in window)) return;

    this.#navObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.toggleAttribute('data-nav-solid', !entry.isIntersecting);
      },
      { rootMargin: `-${NAV_HEIGHT}px 0px 0px 0px`, threshold: 0 }
    );
    this.#navObserver.observe(gallery);
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-aaiko-gallery-track]'));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    /* Size guide. The trigger is a real link to the size-chart file, so it
       still works with this file absent or on a browser without <dialog>;
       intercepting it only when showModal() exists keeps that true. */
    /* Notify me (1210:24484). The sold-out add-to-bag button carries
       data-aaiko-notify-open, so the dead end becomes the one thing still on
       offer. Falls through to the button's normal behaviour when the browser
       has no showModal, same guard the size chart uses. */
    if (target.closest('[data-aaiko-notify-open]')) {
      const dialog = this.querySelector('[data-aaiko-notify-popup]');
      if (dialog instanceof HTMLDialogElement && typeof dialog.showModal === 'function') {
        event.preventDefault();
        dialog.showModal();
        return;
      }
    }
    if (target.closest('[data-aaiko-notify-close]')) {
      event.preventDefault();
      this.querySelector('[data-aaiko-notify-popup]')?.close();
      return;
    }

    const sizeChartOpen = target.closest('[data-aaiko-sizechart-open]');
    if (sizeChartOpen) {
      const dialog = this.querySelector('[data-aaiko-sizechart]');
      if (dialog instanceof HTMLDialogElement && typeof dialog.showModal === 'function') {
        event.preventDefault();
        dialog.showModal();
      }
      return;
    }
    if (target.closest('[data-aaiko-sizechart-close]')) {
      event.preventDefault();
      this.querySelector('[data-aaiko-sizechart]')?.close();
      return;
    }
    /* Click on the backdrop. The dialog's own box is the panel inside it, so a
       hit that lands on the <dialog> element itself is outside the panel. */
    if (target.matches('[data-aaiko-sizechart]')) {
      /** @type {HTMLDialogElement} */ (target).close();
      return;
    }

    /* Shop full look. Same arrangement as the size guide: the trigger stays a
       real anchor to the "Style it with" section, so it goes somewhere without
       this file, and the drawn popup (1210:23595) opens over it when it can. */
    const lookOpen = target.closest('[data-aaiko-lookpop-open]');
    if (lookOpen) {
      const dialog = this.querySelector('[data-aaiko-lookpop]');
      if (dialog instanceof HTMLDialogElement && typeof dialog.showModal === 'function') {
        event.preventDefault();
        dialog.showModal();
      }
      return;
    }
    if (target.closest('[data-aaiko-lookpop-close]')) {
      event.preventDefault();
      this.querySelector('[data-aaiko-lookpop]')?.close();
      return;
    }
    if (target.matches('[data-aaiko-lookpop]')) {
      /** @type {HTMLDialogElement} */ (target).close();
      return;
    }
    if (target.closest('[data-aaiko-lookpop-add]')) {
      event.preventDefault();
      this.#addLook();
      return;
    }

    const option = target.closest('[data-aaiko-option-value]');
    if (option instanceof HTMLElement) {
      event.preventDefault();
      // A sold-out value is drawn as a notify affordance. A sold-out SIZE now
      // carries data-aaiko-notify and stays selectable: selecting it writes the
      // variant into ?variant=, which is the only thing the Back in Stock
      // provider needs before it can render its control in the buy row. Every
      // other sold-out value has nowhere to go and stays inert.
      if (option.dataset.available === 'false' && option.dataset.aaikoNotify !== 'true') return;
      if (option.classList.contains('is-selected')) return;
      this.#selectValue(option);
    }
  };

  /**
   * Radiogroup keyboard contract. Arrows move focus, Enter/Space commits
   * (native <button> behaviour). Selection is deliberately manual rather than
   * automatic-on-arrow: committing fires a section re-render, and firing one
   * per arrow press would make the row unusable from the keyboard.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!KEYS.includes(event.key)) return;
    const current = /** @type {HTMLElement} */ (event.target).closest('[data-aaiko-option-value]');
    if (!(current instanceof HTMLElement)) return;
    const group = current.closest('[data-aaiko-option]');
    if (!group) return;

    const items = /** @type {HTMLElement[]} */ (Array.from(group.querySelectorAll('[data-aaiko-option-value]')));
    const index = items.indexOf(current);
    let next = index;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = items.length - 1;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + items.length) % items.length;
    else next = (index + 1) % items.length;

    event.preventDefault();
    items.forEach((el, i) => el.setAttribute('tabindex', i === next ? '0' : '-1'));
    items[next]?.focus();
  };

  /** One tab stop per group; the checked value owns it, else the first value. */
  #syncRovingTabindex() {
    for (const group of this.querySelectorAll('[data-aaiko-option]')) {
      const items = /** @type {HTMLElement[]} */ (Array.from(group.querySelectorAll('[data-aaiko-option-value]')));
      if (!items.length) continue;
      const checked = items.findIndex((el) => el.getAttribute('aria-checked') === 'true');
      const stop = checked === -1 ? 0 : checked;
      items.forEach((el, i) => el.setAttribute('tabindex', i === stop ? '0' : '-1'));
    }
  }

  /**
   * Reconcile the add-to-bag row without destroying it, so its background can
   * transition between the disabled and buyable states. Returns false when the
   * expected nodes are missing, in which case the caller falls back to the
   * wholesale innerHTML swap and correctness wins over the animation.
   * @param {Element} region  the live region
   * @param {Element} next    the freshly rendered region
   * @returns {boolean} whether the patch was applied
   */
  #patchAddToBag(region, next) {
    const liveRow = region.querySelector('.aaiko-pdp__atb');
    const nextRow = next.querySelector('.aaiko-pdp__atb');
    const liveButton = /** @type {HTMLButtonElement | null} */ (region.querySelector('[data-aaiko-atb]'));
    const nextButton = /** @type {HTMLButtonElement | null} */ (next.querySelector('[data-aaiko-atb]'));
    if (!liveRow || !nextRow || !liveButton || !nextButton) return false;

    // A pending "Added" flash would otherwise re-enable the button 2s later,
    // undoing this patch if the shopper switched to a sold-out size meanwhile.
    if (this.#feedbackTimer) {
      clearTimeout(this.#feedbackTimer);
      this.#feedbackTimer = null;
    }

    liveRow.classList.toggle('aaiko-pdp__atb--empty', nextRow.classList.contains('aaiko-pdp__atb--empty'));
    liveButton.disabled = nextButton.hasAttribute('disabled');
    /* The no-size state is an ATTRIBUTE, not the disabled flag, ever since the
       button went black in every state - and #onSubmit tests it before it will
       POST anything. This row is patched in place rather than replaced, so it
       has to be carried across here too: without it the button keeps sending
       the shopper back to the size row after they have already picked one, and
       nothing ever reaches the cart. */
    liveButton.toggleAttribute('data-aaiko-needs-size', nextButton.hasAttribute('data-aaiko-needs-size'));
    /* Same trap, same fix, for the sold-out state. CLIENT REPORT (feedback
       10/09/2026): "notify when out of stock zit niet in de sticky bar op pdp."
       The label already came across, so the button read "Meld mij" - but the
       attribute that makes it OPEN the notify popup did not, so it stayed a
       submit button and POSTed a sold-out variant to the cart. The sticky bar
       forwards its click to this button, which is where the bar lost notify
       too. The popup itself now rides along in [data-aaiko-sync="notify"]. */
    liveButton.toggleAttribute('data-aaiko-notify-open', nextButton.hasAttribute('data-aaiko-notify-open'));

    const label = (nextButton.textContent || '').trim();
    if (label && liveButton.textContent?.trim() !== label) liveButton.textContent = label;

    return true;
  }

  /** @param {HTMLElement} button */
  async #selectValue(button) {
    const variantId = button.dataset.variantId;
    const sectionId = this.dataset.sectionId;
    const productUrl = this.dataset.productUrl;
    if (!variantId || !sectionId || !productUrl) return;

    const nextUrl = new URL(productUrl, window.location.origin);
    nextUrl.searchParams.set('variant', variantId);

    const requestUrl = new URL(nextUrl.toString());
    requestUrl.searchParams.set('section_id', sectionId);

    // Optimistic: the pressed value reads as chosen before the round trip.
    const group = button.closest('[data-aaiko-option]');
    group?.querySelectorAll('[data-aaiko-option-value]').forEach((el) => {
      el.classList.toggle('is-selected', el === button);
      el.setAttribute('aria-checked', el === button ? 'true' : 'false');
    });

    let doc;
    try {
      const response = await fetch(requestUrl.toString());
      if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
      doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      console.error(error);
      return;
    }

    // Capture before the swap: innerHTML destroys the focused node.
    const hadFocus = this.contains(document.activeElement);

    for (const region of this.querySelectorAll('[data-aaiko-sync]')) {
      const key = /** @type {HTMLElement} */ (region).dataset.aaikoSync;
      const next = doc.querySelector(`[data-aaiko-sync="${key}"]`);
      if (!next) continue;
      // The add-to-bag row is patched in place rather than replaced. Replacing
      // it swapped in a brand new node, so the grey -> black flip had nothing to
      // transition FROM and snapped. Keeping the element alive lets CSS do it,
      // and it preserves the favourite's aria-pressed for free.
      if (key === 'atb' && this.#patchAddToBag(region, next)) continue;
      region.innerHTML = next.innerHTML;
    }

    const hiddenInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-aaiko-variant-id]'));
    if (hiddenInput) {
      const rendered = /** @type {HTMLInputElement | null} */ (doc.querySelector('[data-aaiko-variant-id]'));
      hiddenInput.value = rendered?.value || variantId;
    }

    window.history.replaceState({}, '', nextUrl.toString());
    this.#syncRovingTabindex();
    this.#syncStickyAtbLabel();
    this.#syncStickyPrice();
    this.#syncSheetSizes();

    // Focus survives the innerHTML swap: put it back on the value just chosen.
    const refreshed = this.querySelector(
      `[data-aaiko-option-value="${CSS.escape(String(button.dataset.aaikoOptionValue))}"]`
    );
    if (refreshed instanceof HTMLElement) {
      refreshed.setAttribute('tabindex', '0');
      if (hadFocus && document.activeElement !== refreshed) refreshed.focus({ preventScroll: true });
    }
  }

  /* Send the shopper to the size row: scroll it into view and put focus on the
     first size that can actually be chosen, so the keyboard path and the
     pointer path end in the same place. The row is found by class rather than
     by id because its sync wrapper is `display: contents` and therefore has no
     box to scroll to. */
  #promptForSize() {
    const row = /** @type {HTMLElement | null} */ (
      this.querySelector('[data-aaiko-sync="sizes"] .aaiko-pdp__options--size')
    );
    if (!row) return;
    row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const first =
      row.querySelector('[data-aaiko-option-value]:not([data-available="false"])') ||
      row.querySelector('[data-aaiko-option-value]');
    if (first instanceof HTMLElement) first.focus();
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = /** @type {HTMLFormElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('form')
    );
    if (!form) return;
    /* The notify popup is a <form> inside this element too, and it has its own
       handler. Without this it would land here first and be read as the buy
       form. */
    if (form.hasAttribute('data-aaiko-notify-form')) return;
    event.preventDefault();

    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-aaiko-atb]'));
    if (!button) return;

    /* The black "Kies je maat" button. AAIKO asked for the buy button to stay
       black while no size is chosen, so it can no longer be the inert grey the
       frame draws - a black button that does nothing on click reads as broken.
       There is still nothing to POST, so it takes the shopper to the size row
       instead. */
    if (button.hasAttribute('data-aaiko-needs-size')) {
      this.#promptForSize();
      return;
    }
    if (button.disabled) return;

    const formData = new FormData(form);
    const merchandiseId = String(formData.get('id') || '');
    if (!merchandiseId) return;

    // Horizon re-renders the cart line sections from the same POST.
    const cartSectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);
    if (cartSectionIds.length) formData.append('sections', cartSectionIds.join(','));

    const quantity = Number(formData.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId, quantity }],
        promise: deferred.promise,
      })
    );

    button.disabled = true;

    try {
      const config = fetchConfig('javascript', { body: formData });
      const routes = cartRoutes();
      const response = await fetch(routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message || 'Add to cart failed');

      const cart = await (await fetch(`${routes.cart_url}.js`)).json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'aaiko-pdp',
          sourceId: String(this.dataset.sectionId),
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });

      this.#flashButton(button, themeTranslations().added);
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      button.disabled = false;
    }
  };

  /**
   * @param {HTMLButtonElement} button
   * @param {string} [text]
   */
  #flashButton(button, text) {
    const original = button.textContent;
    if (text) button.textContent = text;
    if (this.#feedbackTimer) clearTimeout(this.#feedbackTimer);
    this.#feedbackTimer = setTimeout(() => {
      button.textContent = original;
      button.disabled = false;
    }, FEEDBACK_MS);
  }

  /** @param {HTMLElement} button */

  /* Add every look item that has a size chosen. One POST to /cart/add.js with
     an `items` array, then the same `shopify:cart:lines-update` with action
     'add' the quick shop announces, which is what makes the drawer refresh
     and open. An item with no size chosen is skipped rather than guessed. */
  #addLook() {
    const dialog = this.querySelector('[data-aaiko-lookpop]');
    const button = this.querySelector('[data-aaiko-lookpop-add]');
    if (!(dialog instanceof HTMLDialogElement) || !(button instanceof HTMLElement)) return;

    const chosen = /** @type {HTMLInputElement[]} */ (
      Array.from(dialog.querySelectorAll('.aaiko-lookpop__radio:checked'))
    );
    const error = dialog.querySelector('.aaiko-lookpop__error');
    if (chosen.length === 0) {
      /* The popup says what went wrong on its own line now, rather than
         recolouring the "add pieces individually" hint, which AAIKO rightly
         pointed out has nothing to do with it (feedback 31/08/2026). */
      dialog.setAttribute('data-aaiko-lookpop-empty', '');
      if (error instanceof HTMLElement) error.hidden = false;
      const first = dialog.querySelector('.aaiko-lookpop__radio:not([disabled])');
      if (first instanceof HTMLElement) first.focus();
      return;
    }
    dialog.removeAttribute('data-aaiko-lookpop-empty');
    if (error instanceof HTMLElement) error.hidden = true;

    const routes = (window.Shopify && window.Shopify.routes) || {};
    const addUrl = routes.cart_add_url || '/cart/add.js';
    const cartUrl = routes.cart_url || '/cart';

    button.setAttribute('aria-busy', 'true');
    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: chosen.map((i) => ({ id: Number(i.value), quantity: 1 })) }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.status) throw new Error(data.description || data.message || 'add failed');
        return fetch(`${cartUrl}.js`).then((r) => r.json());
      })
      .then((cart) => {
        dialog.close();
        document.dispatchEvent(
          new CustomEvent('shopify:cart:lines-update', {
            bubbles: true,
            detail: {
              source: 'aaiko-lookpop',
              action: 'add',
              itemCount: cart.item_count,
              items: cart.items,
            },
          })
        );
      })
      .catch((error) => {
        console.warn('[aaiko-pdp] add all to bag failed', error);
      })
      .finally(() => {
        button.removeAttribute('aria-busy');
      });
  }

  #onScroll = () => {
    const track = this.#track;
    const thumb = /** @type {HTMLElement | null} */ (this.querySelector('[data-aaiko-gallery-thumb]'));
    if (!track || !thumb) return;
    const scrollable = track.scrollWidth - track.clientWidth;
    const ratio = scrollable > 0 ? track.scrollLeft / scrollable : 0;
    const travel = track.clientWidth - thumb.offsetWidth;
    thumb.style.transform = `translateX(${(ratio * travel).toFixed(2)}px)`;
  };
}

if (!customElements.get('aaiko-pdp')) {
  customElements.define('aaiko-pdp', AaikoPdp);
}
